import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CreatedDocument } from './types';

interface SuccessStepProps {
  document: CreatedDocument;
  onReset: () => void;
}

export default function SuccessStep({ document, onReset }: SuccessStepProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center max-w-md mx-auto py-12 animate-scale-in">
      {/* Green checkmark */}
      <div className="mb-6">
        <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center">
          <svg
            className="w-14 h-14 text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
      </div>

      {/* Heading */}
      <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">
        {t('createDocument.success.title')}
      </h2>

      {/* Document number */}
      <p className="text-gray-500 mb-1 text-center">
        {t('createDocument.success.documentNumber')}
      </p>
      <p className="text-2xl font-bold text-gray-900 mb-8">
        #{document.document_number}
      </p>

      {/* Buttons */}
      <div className="flex flex-col gap-4 w-full">
        {document.document_link && (
          <button
            type="button"
            onClick={() => window.open(document.document_link, '_blank')}
            className="flex items-center justify-center gap-2 w-full py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors"
          >
            {t('createDocument.success.openPdf')}
            <svg
              className="w-4 h-4 rtl:rotate-180"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </button>
        )}

        <button
          type="button"
          onClick={onReset}
          className="w-full py-3 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-colors"
        >
          {t('createDocument.success.createAnother')}
        </button>

        <button
          type="button"
          onClick={() => navigate('/documents')}
          className="text-gray-500 underline hover:text-gray-700 transition-colors text-sm"
        >
          {t('createDocument.success.backToDocuments')}
        </button>
      </div>
    </div>
  );
}
